import React, { useMemo, useCallback, useState } from 'react';
import propTypes from 'prop-types';
import { Clock, Eye, Info, TriangleAlert } from 'lucide-react';
import Button from 'tailwind-components/Button/Button';
import Modal from 'tailwind-components/Modal/Modal';
import { cn } from 'lib/utils';
import { sanitizeDirtyHTML } from 'helpers/strings';
import {
  getMaxStalledDaysAmongAppointments,
  getStallAvatarClassNames,
  getStallSeverityPillClassNames,
  STALL_DAYS_RED_GTE,
  isSubstatusActionable
} from 'helpers/appointments/appointments';
import { getSubstatusTooltip } from 'tailwind-components/AppointmentCard/helpers';
import { Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import { getViewAsDisabledReason } from 'helpers/viewAsAgentLink';
import CarrierAppointmentVideo from 'views/MyAppointments/CarrierAppointmentVideo/CarrierAppointmentVideo';

/** Actionable blocking substatus: red accent. Otherwise neutral white. */
const ACTIONABLE_BADGE_CLASS =
  'border-semantic-danger/50 bg-semantic-danger/10 text-semantic-danger';
const NEUTRAL_BADGE_CLASS = 'border-grey-lighter bg-white text-grey-dark';

function getInitialsFromAgentName(name) {
  const trimmed = (name || '').trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    const w = parts[0];
    if (w.length <= 1) return w[0].toUpperCase();
    return (w[0] + w[1]).toUpperCase();
  }
  const first = parts[0][0];
  const last = parts[parts.length - 1][0];
  return `${first}${last}`.toUpperCase();
}

function formatStatesList(statesRaw) {
  if (statesRaw == null || statesRaw === '') return '';
  return String(statesRaw)
    .split(/[,;]/)
    .map(s => s.trim())
    .filter(Boolean)
    .join(', ');
}

function getAppointmentType(type) {
  if (!type || typeof type !== 'string') return '—';
  if (type.includes('Transfer')) return 'Transfer';
  return 'New Appointment';
}

function getPendingCustomNote(appt) {
  if (!appt || typeof appt !== 'object') return '';
  const raw = appt.Custom_Note_for_Agent ?? appt.custom_note_for_agent;
  if (raw == null) return '';
  return String(raw);
}

function noteHasVisibleContent(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return false;
  const stripped = s
    .replaceAll(/<[^>]+>/g, ' ')
    .replaceAll(/&nbsp;/gi, ' ')
    .trim();
  return stripped.length > 0;
}

function getCarrierNewAppointmentVideoUrl(carriersArray, carrierName) {
  if (!carrierName || !Array.isArray(carriersArray)) return '';
  const carrier = carriersArray.find(c => c.name === carrierName);
  return carrier?.newAppointmentsVideoURL || '';
}

function pendingRowHasInstructionsOrVideo(appt, carriersArray) {
  const noteOk = noteHasVisibleContent(getPendingCustomNote(appt));
  const videoUrl = getCarrierNewAppointmentVideoUrl(
    carriersArray,
    appt.Carrier_Name
  );
  const videoOk =
    !!videoUrl &&
    getAppointmentType(appt.Appointment_Type) === 'New Appointment';
  return noteOk || videoOk;
}

function AgencyPendingAgentCard({ agentId, agentName, agencyName, appointments }) {
  const user = useSelector(state => state.user.zohoData);
  const carriersArray = useSelector(state => state.appointments.carriersArray);
  const [instructionsAppt, setInstructionsAppt] = useState(null);

  const instructionsVideoUrl = useMemo(
    () =>
      instructionsAppt
        ? getCarrierNewAppointmentVideoUrl(
            carriersArray,
            instructionsAppt.Carrier_Name
          )
        : '',
    [instructionsAppt, carriersArray]
  );

  const instructionsShowVideo = useMemo(
    () =>
      !!instructionsVideoUrl &&
      instructionsAppt &&
      getAppointmentType(instructionsAppt.Appointment_Type) ===
        'New Appointment',
    [instructionsVideoUrl, instructionsAppt]
  );

  const instructionsNoteRaw = instructionsAppt
    ? getPendingCustomNote(instructionsAppt)
    : '';
  const instructionsHasNote = noteHasVisibleContent(instructionsNoteRaw);
  const maxStall = useMemo(
    () => getMaxStalledDaysAmongAppointments(appointments),
    [appointments]
  );

  const initials = useMemo(
    () => getInitialsFromAgentName(agentName),
    [agentName]
  );

  const openCount = appointments.length;
  const subtitle =
    openCount === 1 ? '1 open request' : `${openCount} open requests`;

  const rawAgentId = appointments[0]?.Agent_ID
    ? String(appointments[0].Agent_ID)
    : agentId;

  const handleViewAgent = useCallback(() => {
    if (!rawAgentId) return;
    window.open(
      `/carrier-appointments?va=${encodeURIComponent(rawAgentId)}`,
      '_blank'
    );
  }, [rawAgentId]);

  const showViewAgent = Boolean(agentId);
  const isSelfTarget = String(user.id) === String(rawAgentId);
  const viewAsDisabledReason = getViewAsDisabledReason({
    requesterZohoData: user,
    isSelfTarget
  });

  return (
    <article
      className={cn(
        'flex w-full min-w-0 flex-col overflow-hidden border border-black/15 rounded-xl',
        'bg-white shadow-[0_2px_6px_rgba(0,0,0,0.06)]'
      )}
    >
      <header className='flex items-start justify-between gap-3 border-b border-grey-lighter px-4 py-3.5 sm:px-5'>
        <div className='flex min-w-0 flex-1 gap-3'>
          <div
            className={cn(
              'flex size-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold',
              getStallAvatarClassNames(maxStall)
            )}
            aria-hidden
          >
            {initials}
          </div>
          <div className='min-w-0 flex-1'>
            <h3 className='m-0 font-sans text-base font-semibold leading-snug text-primary-darkened sm:text-lg'>
              {agentName || 'Unknown agent'}
            </h3>
            {agencyName && (
              <p className='m-0 mt-0.5 truncate text-xs text-grey'>{agencyName}</p>
            )}
            <p className='m-0 mt-0.5 text-sm text-grey'>{subtitle}</p>
          </div>
        </div>
        {showViewAgent && (
          <span title={viewAsDisabledReason || undefined}>
            <Button
              variant='secondary'
              size='xsmall'
              text='View Agent'
              icon={<Eye />}
              className='shrink-0'
              onClick={handleViewAgent}
              disabled={Boolean(viewAsDisabledReason)}
              data-tour='appt-center-view-agent'
            />
          </span>
        )}
      </header>

      {/* min-width ~704px keeps column proportions; narrower viewports scroll horizontally */}
      <div className='overflow-x-auto [-webkit-overflow-scrolling:touch]'>
        <table
          className='w-full min-w-[704px] border-collapse text-left'
          style={{ tableLayout: 'fixed' }}
        >
          <colgroup>
            <col style={{ width: '25%' }} /> {/* Carrier */}
            <col style={{ width: '18%' }} /> {/* Appointment type */}
            <col style={{ width: '30%' }} /> {/* Blocking action */}
            <col style={{ width: '11%' }} /> {/* Instructions */}
            <col style={{ width: '16%' }} /> {/* Stalled */}
          </colgroup>
          <thead>
            <tr className='border-b border-grey-lighter bg-grey-background/40'>
              <th className='px-4 py-2.5 whitespace-nowrap text-[10px] font-semibold tracking-wide text-grey uppercase sm:px-5'>
                Carrier
              </th>
              <th className='px-3 py-2.5 whitespace-nowrap text-[10px] font-semibold tracking-wide text-grey uppercase'>
                Appointment type
              </th>
              <th className='px-3 py-2.5 whitespace-nowrap text-[10px] font-semibold tracking-wide text-grey uppercase'>
                Blocking action
              </th>
              <th className='px-3 py-2.5 whitespace-nowrap text-[10px] font-semibold tracking-wide text-grey uppercase'>
                Instructions
              </th>
              <th className='px-4 py-2.5 whitespace-nowrap text-[10px] font-semibold tracking-wide text-grey uppercase sm:px-5'>
                Stalled
              </th>
            </tr>
          </thead>
          <tbody>
            {appointments.map((appt, rowIndex) => {
              const days = appt.Stalled_Days;
              const actionable = isSubstatusActionable(appt.Substatus);
              const stallPillClass = getStallSeverityPillClassNames(days);
              const blockingActionPillClass = actionable
                ? ACTIONABLE_BADGE_CLASS
                : NEUTRAL_BADGE_CLASS;
              const statesFormatted = formatStatesList(appt.States);
              const ticket = appt.Ticket_ID || '';
              const canOpenInstructions = pendingRowHasInstructionsOrVideo(
                appt,
                carriersArray
              );

              return (
                <tr
                  key={`${String(appt.Ticket_ID)}-${String(
                    appt.Carrier_Name
                  )}-${rowIndex}`}
                  className='border-b border-grey-lighter last:border-b-0'
                >
                  <td className='max-w-0 px-4 py-3 align-middle sm:px-5'>
                    <div className='truncate font-semibold text-primary-darkened'>
                      {appt.Carrier_Name || '—'}
                    </div>
                    {(ticket || statesFormatted) && (
                      <div className='mt-1 truncate text-xs leading-snug text-grey'>
                        {[ticket, statesFormatted].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </td>
                  <td className='max-w-0 px-3 py-3 align-middle text-sm text-black'>
                    <div className='truncate'>
                      {getAppointmentType(appt.Appointment_Type)}
                    </div>
                  </td>
                  <td className='max-w-0 px-3 py-3 align-middle'>
                    {appt.Substatus ? (
                      <Tooltip
                        title={getSubstatusTooltip(appt.Substatus)}
                        placement='left'
                        arrow
                        slotProps={{
                          tooltip: {
                            sx: {
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              padding: '4px 8px',
                              textAlign: 'center',
                              color: '#fff'
                            }
                          }
                        }}
                      >
                        <span
                          className={cn(
                            'inline-flex max-w-full min-w-0 rounded-full border px-2.5 py-1 text-left text-xs font-semibold leading-snug',
                            blockingActionPillClass,
                            actionable && 'items-center gap-1'
                          )}
                        >
                          {actionable && (
                            <TriangleAlert
                              className='size-3.5 shrink-0'
                              aria-hidden
                            />
                          )}
                          <span
                            className={cn(
                              'min-w-0 truncate',
                              actionable && 'pt-0.5'
                            )}
                          >
                            {appt.Substatus}
                          </span>
                        </span>
                      </Tooltip>
                    ) : (
                      <span className='text-sm text-grey'>—</span>
                    )}
                  </td>
                  <td className='max-w-0 px-3 py-3 align-middle'>
                    <Button
                      variant='secondary'
                      size='xsmall'
                      text='View'
                      icon={<Info className='size-3.5' aria-hidden />}
                      className='shrink-0'
                      disabled={!canOpenInstructions}
                      onClick={() => setInstructionsAppt(appt)}
                    />
                  </td>
                  <td className='max-w-0 px-4 py-3 align-middle sm:px-5'>
                    {typeof days === 'number' ? (
                      <div className='flex min-w-0 flex-nowrap items-center gap-1.5'>
                        <span
                          className={cn(
                            'inline-flex shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold whitespace-nowrap',
                            stallPillClass
                          )}
                        >
                          {days} {days === 1 ? 'day' : 'days'}
                        </span>
                        {days >= STALL_DAYS_RED_GTE && (
                          <span
                            className={cn(
                              'flex min-w-0 shrink items-center gap-1 overflow-hidden rounded-full border px-2.5 py-1 text-xs font-semibold whitespace-nowrap',
                              stallPillClass
                            )}
                          >
                            <Clock className='size-3.5 shrink-0' aria-hidden />
                            <span className='min-w-0 truncate pt-0.5'>
                              Expiring soon
                            </span>
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className='text-sm text-grey'>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal
        active={instructionsAppt != null}
        title={
          instructionsAppt
            ? `Instructions · ${instructionsAppt.Carrier_Name || 'Carrier'}`
            : ''
        }
        closeModal={() => setInstructionsAppt(null)}
        size='large'
        showScrollbar
      >
        <div className='flex min-w-0 flex-col gap-2 px-1 pb-2'>
          {instructionsHasNote && (
            <div
              className='text-base [&_a]:font-medium [&_a]:text-primary-emphasis-light [&_a]:no-underline hover:[&_a]:underline [&_ol]:my-1.5 [&_ol]:pl-[18px] [&_p]:my-1.5 [&_p]:first:mt-0 [&_ul]:my-1.5 [&_ul]:pl-[18px]'
              dangerouslySetInnerHTML={sanitizeDirtyHTML(instructionsNoteRaw)}
            />
          )}
          {instructionsShowVideo && (
            <CarrierAppointmentVideo src={instructionsVideoUrl} />
          )}
        </div>
      </Modal>
    </article>
  );
}

AgencyPendingAgentCard.propTypes = {
  agentId: propTypes.string,
  agentName: propTypes.string,
  agencyName: propTypes.string,
  appointments: propTypes.arrayOf(propTypes.object).isRequired
};

export default AgencyPendingAgentCard;
